import { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { Queue } from '@rossum/api-client/queues';
import {
  Alert,
  Button,
  CircularProgress,
  Typography,
} from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';
import { usePatchQueue } from '../../../../queues/hooks/usePatchQueue';

const isAutomationDisabledConflict = (queue: Queue) =>
  !queue.automationEnabled && queue.automationLevel !== 'never';

type AutomationDisabledAlertProps = {
  queue: Queue;
};

const codeText = (chunks: ReactNode[]) => (
  <Typography
    component="span"
    variant="inherit"
    sx={{
      backgroundColor: 'action.hover',
      borderRadius: borderRadius('s'),
      fontFamily: 'monospace',
      fontSize: '0.85em',
      px: 0.5,
      py: 0.25,
    }}
  >
    {chunks}
  </Typography>
);

export const AutomationDisabledAlert = ({
  queue,
}: AutomationDisabledAlertProps) => {
  const intl = useIntl();
  const { mutate: patchQueue, isLoading } = usePatchQueue();

  if (!isAutomationDisabledConflict(queue)) return null;

  return (
    <Alert
      severity="warning"
      action={
        <Button
          variant="text"
          color="inherit"
          disabled={isLoading}
          startIcon={
            isLoading ? <CircularProgress size={16} color="inherit" /> : null
          }
          onClick={() => {
            patchQueue({
              queueId: queue.id,
              payload: { automationEnabled: true },
            });
          }}
          sx={{ flexShrink: 0 }}
        >
          {intl.formatMessage({
            id: 'containers.settings.queues.queue.automation.automationDisabledAlert.button',
          })}
        </Button>
      }
    >
      <Typography component="span" variant="body2">
        {intl.formatMessage(
          {
            id: 'containers.settings.queues.queue.automation.automationDisabledAlert.description',
          },
          { code: codeText }
        )}
      </Typography>
    </Alert>
  );
};
